import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { SoSwitchUI } from 'so-components-kit';
import { TrunkBaseConfigurationService } from '@core/services/trunk-base-configuration/trunk-base-configuration.service';
import { SharedModule } from '@shared/shared.module';
import { SharedStateSelectedFiltersServiceNew } from '@modules/configuration-ia/services/signals/global/selected-filters/shared-state-selected-filters-NEW.service';
import { environment } from 'src/environments/environment';
import { GetWsSyncStateUseCase } from '../../domain/use-cases/get-ws-sync-state.use-case';
import { SaveWsSyncStateUseCase } from '../../domain/use-cases/save-ws-sync-state.use-case';

const ALLOWED_URL_FRAGMENTS = ['/promotions', '/kbd'];

@Component({
  selector: 'app-ws-selector-ia-switch',
  standalone: true,
  imports: [CommonModule, SharedModule, SoSwitchUI],
  templateUrl: './ws-selector-ia-switch.component.html',
  styleUrl: './ws-selector-ia-switch.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WsSelectorIaSwitchComponent {
  private readonly trunkBaseConfigurationService = inject(TrunkBaseConfigurationService);
  private readonly sharedStateSelectedFiltersService = inject(SharedStateSelectedFiltersServiceNew);
  private readonly getWsSyncStateUseCase = inject(GetWsSyncStateUseCase);
  private readonly saveWsSyncStateUseCase = inject(SaveWsSyncStateUseCase);
  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url }
  );

  public readonly idMarca = input<string | null>(null);
  public readonly idSubmarca = input<string | null>(null);
  public readonly marca = input<string | null>(null);
  public readonly submarca = input<string | null>(null);

  public readonly visible = computed(() => {
    if (!this.trunkBaseConfigurationService.CIAContenidoWS) return false;
    if (!this.isInAllowedRoute()) return false;
    const id = this.idMarca();
    const isStellantis = id === environment.STELLANTIS.id
      || id === this.sharedStateSelectedFiltersService.STELLANTIS_ID;
    if (!isStellantis) return false;
    return !!this.idSubmarca();
  });

  public readonly active = computed(() =>
    this.getWsSyncStateUseCase.execute(this.idMarca() ?? '', this.idSubmarca() ?? '')()
  );

  public readonly pending = computed(() =>
    this.getWsSyncStateUseCase.pending(this.idMarca() ?? '', this.idSubmarca() ?? '')()
  );

  onToggle(checked: boolean): void {
    if (this.pending()) return;
    const idMarca = this.idMarca();
    const idSubmarca = this.idSubmarca();
    if (!idMarca || !idSubmarca) return;
    this.saveWsSyncStateUseCase.execute({
      idMarca,
      marca: this.resolveMarcaName(),
      idSubmarca,
      submarca: this.submarca() ?? '',
      active: checked,
    });
  }

  private resolveMarcaName(): string {
    // El switch sólo es visible para Stellantis (lo gatea visible()), así que
    // si la barra no logró resolver el nombre (p.ej. en KDB donde la marca viene
    // pre-seleccionada del userInfo y no hay dropdown que dispare setBaseBrandSelectedText),
    // caemos a STELLANTIS_TEXT del environment / del estado compartido.
    const fromInput = this.marca();
    if (fromInput) return fromInput;
    const fromState = this.sharedStateSelectedFiltersService.$baseBrandSelectedText;
    if (fromState) return fromState;
    return this.sharedStateSelectedFiltersService.STELLANTIS_TEXT;
  }

  private isInAllowedRoute(): boolean {
    const url = this.currentUrl();
    return ALLOWED_URL_FRAGMENTS.some(fragment => url.includes(fragment));
  }
}
